import {
  defined,
  ScreenSpaceEventType,
  ScreenSpaceEventHandler,
  CallbackProperty,
  Cartesian3,
  Color,
  SceneTransforms,
  Cartesian2,
  PolylineDashMaterialProperty,
} from "@cesium/engine";

import MeasurementLengthHorizontal from "../measurement/MeasurementLengthHorizontal";
import Utils from "../basic/Utils";

/**
 * 수평 거리 측정을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input class for horizontal length measurement using mouse/keyboard input.
 * @alias InteractionLengthHorizontal
 * @constructor
 *
 * @param {object} langthHorizontalOption 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Function} [langthHorizontalOption.startCallback] 첫 번째 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating first point.
 * @param {Function} [langthHorizontalOption.addCallback] 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating a point.
 * @param {Function} [langthHorizontalOption.endCallback] 마지막 점을 생성할 때 실행되는 콜백 함수. Callback function executed when creating last point.
 * @param {MeasurementLengthHorizontal}  [langthHorizontalOption.measurementLengthHorizontal] 수평 거리 및 영역 표시 클래스.<br> horizontal calculation and area display class.
 * @example
 * // 샌드캐슬 예제 'DiTAP - Horizontal Length Measurement' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const measurementLengthHorizontal = new Ditap.MeasurementLengthHorizontal(viewer);
 *
 * // InteractionLengthHorizontal on/off 실행 시 MeasurementLengthHorizontal 초기화 필수
 * // measurementLengthHorizontal 초기화 방법
 * // 1. InteractionLengthHorizontal 생성 후, MeasurementLengthHorizontal, callback 함수 대입
 * const interactionLengthHorizontal = new Ditap.InteractionLengthHorizontal();
 * interactionLengthHorizontal.measurementLengthHorizontal = measurementLengthHorizontal;
 * interactionLengthHorizontal.startCallback = () => {};
 * interactionLengthHorizontal.addCallback = () => {};
 * interactionLengthHorizontal.endCallback = () => {};
 *
 * // 2. InteractionLengthHorizontal 생성 시, langthHorizontalOption 지정
 * const langthHorizontalOption = {
 *    measurementLengthHorizontal: measurementLengthHorizontal,
 *    startCallback: () => {}
 *    addCallback: () => {}
 *    endCallback: () => {}
 * };
 * const interactionLengthHorizontal = new Ditap.InteractionLengthHorizontal(langthHorizontalOption);
 */
class InteractionLengthHorizontal {
  constructor(langthHorizontalOption) {
    this._measurementLengthHorizontal = undefined;
    if (
      langthHorizontalOption &&
      langthHorizontalOption.measurementLengthHorizontal instanceof
        MeasurementLengthHorizontal
    ) {
      this.measurementLengthHorizontal =
        langthHorizontalOption.measurementLengthHorizontal; //원본 클래스
    }
    this._handler = undefined;
    this._moveEntities = []; //move 시 생성되는 Entity Id 배열
    this._isOn = false; // 핸들러 실행 여부
    this._mousePosition = undefined;
    this._infoLabelEntity = undefined;
    this._lengthLabelEntity = undefined;
    this._keyDownEvent = undefined;
    this._buttonElement = undefined;
    this._polylinePrimitive = undefined;
    this._polylinePrimitive = undefined;
    this.cancelBtn = undefined; // 핸들러 취소 버튼
    this.removeBtn = undefined; // 한점제거 버튼
    this._isMobile = undefined; // 모바일 여부
    this.drawingPolylineEntities = [];
    this.drawingPointEntities = [];

    if (langthHorizontalOption) {
      if (langthHorizontalOption.startCallback instanceof Function) {
        this._startCallback = langthHorizontalOption.startCallback;
      } else {
        this._startCallback = undefined;
      }
      if (langthHorizontalOption.addCallback instanceof Function) {
        this._addCallback = langthHorizontalOption.addCallback;
      } else {
        this._addCallback = undefined;
      }
      if (langthHorizontalOption.endCallback instanceof Function) {
        this._endCallback = langthHorizontalOption.endCallback;
      } else {
        this._endCallback = undefined;
      }
    }
  }

  /**
   * 수평 거리 계산 및 영역 표시 클래스.<br>
   * Horizontal distance calculation and area display class.
   * @type {MeasurementLengthHorizontal}
   */
  set measurementLengthHorizontal(measurementLengthHorizontal) {
    if (
      measurementLengthHorizontal instanceof MeasurementLengthHorizontal ||
      measurementLengthHorizontal === undefined
    ) {
      this._measurementLengthHorizontal = measurementLengthHorizontal; // 원본 클래스
    } else {
      console.error("Type of parameter doesn't match");
    }
  }
  get measurementLengthHorizontal() {
    return this._measurementLengthHorizontal;
  }

  /*
   * 마우스 무브 시 생성된 엔티티 배열.</br>
   * An array of entities created by mouse movement.
   * @type {Entity[]}
   */
  set moveEntities(moveEntities) {
    this._moveEntities = moveEntities;
  }
  get moveEntities() {
    return this._moveEntities;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  set isOn(boolean) {
    this._isOn = boolean;
  }
  get isOn() {
    return this._isOn;
  }

  /*
   * 카메라 시점 변경 시 실행 이벤트. </br>
   * Events to run when changing camera viewpoint in window.
   * @type {Function}
   */
  set cameraChangedEvent(cameraChangedEvent) {
    this._cameraChangedEvent = cameraChangedEvent;
  }
  get cameraChangedEvent() {
    return this._cameraChangedEvent;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 마우스 무브 시 생성된 동적 포인트 엔티티.</br>
   * Dynamic point Entity generated on mouse move.
   * @type {Entity}
   */
  set movePoint(movePoint) {
    this._movePoint = movePoint;
  }
  get movePoint() {
    return this._movePoint;
  }

  /*
   * 길이 라벨 라벨 요소.</br>
   * Length label element.
   * @type {Element}
   */
  set lengthLabelEntity(lengthLabelEntity) {
    this._lengthLabelEntity = lengthLabelEntity;
  }
  get lengthLabelEntity() {
    return this._lengthLabelEntity;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }
  get keyDownEvent() {
    return this._keyDownEvent;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼 요소.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }
  get buttonElement() {
    return this._buttonElement;
  }

  /*
   * 마우스 무브 시 생성된 동적 폴리라인 프리미티브.</br>
   * Dynamic polyline primitive generated on mouse move.
   * @type {Primitive}
   */
  set polylinePrimitive(polylinePrimitive) {
    this._polylinePrimitive = polylinePrimitive;
  }
  get polylinePrimitive() {
    return this._polylinePrimitive;
  }

  /**
   * 첫 번째 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating first point.
   * @type {Function}
   */
  set startCallback(startCallback) {
    if (startCallback instanceof Function) {
      this._startCallback = startCallback;
    } else {
      this.startCallback = undefined;
    }
  }
  get startCallback() {
    return this._startCallback;
  }

  /**
   * 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating a point.
   * @type {Function}
   */
  set addCallback(addCallback) {
    if (addCallback instanceof Function) {
      this._addCallback = addCallback;
    } else {
      this.addCallback = undefined;
    }
  }
  get addCallback() {
    return this._addCallback;
  }

  /**
   * 마지막 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating last point.
   * @type {Function}
   */
  set endCallback(endCallback) {
    if (endCallback instanceof Function) {
      this._endCallback = endCallback;
    } else {
      this.endCallback = undefined;
    }
  }
  get endCallback() {
    return this._endCallback;
  }

  /**
   * 수평거리 측정 핸들러 실행. (MeasurementLengthHorizontal 초기화 필수.)</br>
   * Execute horizontal measurement handler. (MeasurementLengthHorizontal initialization is required.)
   */
  on() {
    if (this.measurementLengthHorizontal && !this.isOn) {
      this._isMobile = Utils.detectDeviceType();
      this.measurementLengthHorizontal.viewer.currentEvent = this;
      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      this.handler = new ScreenSpaceEventHandler(
        this.measurementLengthHorizontal.viewer.canvas
      );

      this.isOn = true;

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.measurementLengthHorizontal.viewer.container.querySelector(
          ".cesium-viewer"
        ),
        "왼 클릭 또는 터치하여",
        "수평거리를 측정할 선을 그려주세요",
        false,
        true
      );

      // LABEL - 설명 라벨 생성
      this.infoLabel = Utils.createInfoLabel(
        this.measurementLengthHorizontal.viewer.container.querySelector(
          ".cesium-viewer"
        ),
        "왼 클릭하여 수평거리를 측정할 선을",
        "그려주세요",
        false,
        true
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.removeBtn = this.mobileInfoLabel.removeBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.infoLabel.style.display = "none";
        this.createMobileEvent();
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      // 핸들러 - 실행
      this.createLeftClickHandler();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createCameraMoveEvent();
      this.createResizeEvent();
    }
  }

  /*
   * 카메라 시점 이동 시 이벤트 생성. </br>
   * Create an event when moving the camera point of view.
   */
  createCameraMoveEvent() {
    const viewer = this.measurementLengthHorizontal.viewer;
    this.cameraChangedEvent = () => {
      this.moveLengthLabel();
    };
    viewer?.scene.camera.changed.addEventListener(this.cameraChangedEvent);
  }

  /**
   * 수평 거리 측정 핸들러 종료. (MeasurementLengthHorizontal 초기화 필수.)</br>
   * End horizontal measurement handler. (MeasurementLengthHorizontal initialization is required.)
   */
  off() {
    if (this.measurementLengthHorizontal && this.isOn) {
      const viewer = this.measurementLengthHorizontal.viewer;

      for (const entity of this.drawingPointEntities) {
        viewer.entities.remove(entity);
      }

      for (const entity of this.drawingPolylineEntities) {
        viewer.entities.remove(entity);
      }

      this.measurementLengthHorizontal.viewer.currentEvent = undefined;
      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");
      // ENTITY - MOVE시 생성된 엔티티 제거
      this.moveEntities.forEach((moveEntity) => {
        viewer.entities.remove(moveEntity);
      });

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      if (this.lengthLabelEntity) {
        this.lengthLabelEntity.remove();
      }

      // PRIMITIVE - 다이나믹 폴리라인 프리미티브 제거
      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
      }
      window.removeEventListener("keydown", this.keyDownEvent);

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      // 카메라 이벤트 삭제
      viewer.scene.camera.changed.removeEventListener(this.cameraChangedEvent);

      this.handler = undefined;
      this.moveEntities = []; //move 시 생성되는 Entity Id 배열
      this.isOn = false; // 핸들러 실행 여부
      this.mousePosition = undefined;
      this.lengthLabelEntity = undefined;
      this.keyDownEvent = undefined;
      this.polylinePrimitive = undefined;
      this.measurementLengthHorizontal = undefined;
      this.cameraChangedEvent = undefined;
      this.cameraChangedEvent = undefined;
      this.cancelBtn = undefined;
      this.removeBtn = undefined;
      this._isMobile = undefined;
      this.drawingPointEntities = [];
      this.drawingPolylineEntities = [];
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;
            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.removeBtn = this.mobileInfoLabel.removeBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.lengthLabelEntity.classList.add("hide");
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            for (const entity of this.moveEntities) {
              this.measurementLengthHorizontal.viewer.entities.remove(entity);
            }
            if (this.polylinePrimitive) {
              this.measurementLengthHorizontal.viewer.scene.primitives.remove(
                this.polylinePrimitive
              );
            }

            this.moveEntities = [];
            this.polylinePrimitive = undefined;

            this.createMobileEvent();
          } else {
            this.lengthLabelEntity.classList.remove("hide");
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();

            // this.moveLengthLabel();
          }
        }
      }

      this.moveLengthLabel();
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.destroy();
      })
    );

    this.removeBtn.addEventListener(
      "click",
      (this.removeBtn = () => {
        this.removePoint();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
      this.cancelBtn = undefined;
      this.cancelEvent = undefined;
    }
    if (this.removeEvent) {
      this.removeBtn.removeEventListener("click", this.removeEvent);
      this.removeBtn = undefined;
      this.removeEvent = undefined;
    }
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.abort();
    this.off();
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const viewer = this.measurementLengthHorizontal.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const pickPosition = Utils.pickPosition(
        e.position,
        this.measurementLengthHorizontal.viewer
      );
      const positions = this.measurementLengthHorizontal.positions;

      if (defined(pickPosition)) {
        this.mousePosition = pickPosition;
        // 점 생성 시 콜백함수 실행
        if (this.addCallback && this.measurementLengthHorizontal) {
          this.addCallback(this.measurementLengthHorizontal);
        }

        if (positions.length < 1) {
          // 선택 지점에 포인트 생성
          const startPoint = Utils.createPoint({
            viewer: viewer,
            position: pickPosition,
          });
          this.drawingPointEntities.push(startPoint);

          // 시작 점에서 수평 좌표값 계산
          this.measurementLengthHorizontal.horizontalPosition = this.measurementLengthHorizontal.getHorizontalPosition(
            pickPosition,
            this.mousePosition
          );

          // 시작 점에서 수평거리 계산
          const distance = this.measurementLengthHorizontal.calculateLength(
            this.measurementLengthHorizontal.horizontalPosition,
            this.mousePosition
          );

          // 원본 클래스에 시작좌표, 시작포인트, 수평거리 저장
          this.measurementLengthHorizontal.positions.push(pickPosition);
          this.measurementLengthHorizontal.pointEntities.push(startPoint);
          this.measurementLengthHorizontal.distance = distance;

          // LABEL - 동적 라벨 생성
          const label = Utils.createTotalLabel(viewer, distance);
          this.lengthLabelEntity = label;
          this.measurementLengthHorizontal.labelHeight = label.clientHeight;
          Utils.changeLabelLocation(
            viewer,
            Cartesian3.midpoint(
              this.measurementLengthHorizontal.horizontalPosition,
              pickPosition,
              new Cartesian3()
            ),
            label,
            label.clientHeight
          );

          let first = true;
          // POINT - 수직 point 생성
          const verticalPoint = Utils.createPoint({
            viewer: viewer,
            position: new CallbackProperty(() => {
              if (first) {
                first = false;
                return this.measurementLengthHorizontal.horizontalPosition;
              }
              if (!this._isMobile) {
                return this.measurementLengthHorizontal.horizontalPosition;
              }
            }, false),
          });

          // POLYLINE - 동적 시작<->수직, 대쉬
          const verticalPolyline = Utils.createPolyline({
            viewer: viewer,
            positions: new CallbackProperty(() => {
              if (!this._isMobile) {
                return [
                  pickPosition,
                  this.measurementLengthHorizontal.horizontalPosition,
                ];
              }
            }, false),
            material: new PolylineDashMaterialProperty({
              color: Color.WHITE,
            }),
          });

          if (this.polylinePrimitive) {
            viewer.scene.primitives.remove(this.polylinePrimitive);
          }

          // // POLYLINE - 동적 수직 포인트<->수평 라인, 일반
          const horizontalPolyline = Utils.createPolyline({
            viewer: viewer,
            positions: new CallbackProperty(() => {
              if (!this._isMobile) {
                const left = this.measurementLengthHorizontal
                  .horizontalPosition;
                const right = this.mousePosition;
                const leftCart = SceneTransforms.wgs84ToWindowCoordinates(
                  viewer.scene,
                  left
                );
                const rightCart = SceneTransforms.wgs84ToWindowCoordinates(
                  viewer.scene,
                  right
                );
                const distance = Cartesian2.distance(leftCart, rightCart);
                const resultArray = [left];

                if (distance < 100) {
                  resultArray.push(left);
                } else {
                  const middle = Cartesian3.lerp(
                    left,
                    right,
                    9 / 10,
                    new Cartesian3()
                  );
                  resultArray.push(middle);
                }

                return resultArray;
              }
            }, false),
          });

          this.drawingPolylineEntities.push(verticalPolyline);
          this.drawingPolylineEntities.push(horizontalPolyline);
          this.drawingPointEntities.push(verticalPoint);
          // this.moveEntities.push(label);

          // 첫번째 점 생성 시 콜백함수 실행
          if (this.startCallback && this.measurementLengthHorizontal) {
            this.startCallback(this.measurementLengthHorizontal);
          }

          this._isMobile = Utils.detectDeviceType();
          if (this._isMobile) {
            this.lengthLabelEntity.classList.add("hide");
          }
        } else {
          {
            this.measurementLengthHorizontal.positions.push(pickPosition);
            this.measurementLengthHorizontal.createLengthHorizontal();

            // 마지막 점 생성 시 콜백함수 실행
            if (this.endCallback && this.measurementLengthHorizontal) {
              this.endCallback(this.measurementLengthHorizontal);
            }

            this.off();
          }
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 길이 라벨 위치 변경.<br>
   * Change the position of the length label.
   */
  moveLengthLabel() {
    if (this.lengthLabelEntity && !this._isMobile) {
      const centerPosition = Cartesian3.midpoint(
        this.measurementLengthHorizontal.horizontalPosition,
        this.mousePosition,
        new Cartesian3()
      );

      const labelHeight = this.lengthLabelEntity.clientHeight;
      this.measurementLengthHorizontal.labelHeight = labelHeight;
      this.measurementLengthHorizontal.centerPosition = centerPosition;

      Utils.changeLabelLocation(
        this.measurementLengthHorizontal.viewer,
        centerPosition,
        this.lengthLabelEntity,
        labelHeight
      );
    }
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.measurementLengthHorizontal.viewer;
    const handler = this.handler;

    // MOUSE_MOVE
    handler.setInputAction((e) => {
      if (this.isOn) {
        const movePosition = Utils.pickPosition(e.endPosition, viewer);
        if (defined(movePosition)) {
          this.mousePosition = movePosition;

          // 도움말 라벨 좌표 이동
          Utils.changeLabelLocation(
            viewer,
            this.mousePosition,
            this.infoLabelEntity
          );

          const startPosition = this.measurementLengthHorizontal.positions[0];

          if (startPosition) {
            // 시작 점에서 수평 좌표값 계산
            this.measurementLengthHorizontal.horizontalPosition = this.measurementLengthHorizontal.getHorizontalPosition(
              startPosition,
              this.mousePosition
            );

            // 시작 점에서 수평거리 계산
            let distance = this.measurementLengthHorizontal.calculateLength(
              this.measurementLengthHorizontal.horizontalPosition,
              this.mousePosition
            );

            distance = Utils.printCommaSeparator(distance);
            this.lengthLabelEntity.querySelector(
              ".ditap-card-title"
            ).textContent = `${distance}m`;

            this.moveLengthLabel();

            // 원본 클래스에 DISTANCE값 저장
            this.measurementLengthHorizontal.distance = distance;

            if (this.polylinePrimitive) {
              viewer.scene.primitives.remove(this.polylinePrimitive);
            }

            // // POLYLINE - 동적 수직 포인트<->수평 라인, 일반
            const horizontalPolyline = Utils.createPolylinePrimitive({
              viewer: viewer,
              positions: [
                this.measurementLengthHorizontal.horizontalPosition,
                this.mousePosition,
              ],
            });

            this.polylinePrimitive = horizontalPolyline;
          }
        } else {
          this.infoLabelEntity.classList.add("hide");
        }
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      // 한 점 이상일 경우
      if (this.measurementLengthHorizontal.positions.length > 0) {
        this.removePoint();
      } else {
        this.abort();
        this.off();
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }

  /**
   * 측정 중 중단.</br>
   * Interrupt during measurement.
   */
  abort() {
    // measurementLengthHorizontal 삭제
    this.measurementLengthHorizontal?.destroy();
    // this.measurementLengthHorizontal = undefined;
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      // ESC 클릭 : Entity 제거, 모든 핸들러 종료

      this.abort();
      this.off();
    } else if (event.key === "Backspace") {
      this.removePoint();
    }
  }

  /*
   * 측정 중인 한 점 제거.<br>
   * Remove the point being measured.
   */
  removePoint() {
    const viewer = this.measurementLengthHorizontal.viewer;
    // Backspace : 한 점 제거

    // 한 점 이상일 경우
    if (this.measurementLengthHorizontal.positions.length > 0) {
      // move 시 생성된 Label 제거
      for (const entity of this.moveEntities) {
        viewer.entities.remove(entity);
      }

      for (const entity of this.drawingPointEntities) {
        viewer.entities.remove(entity);
      }
      for (const entity of this.drawingPolylineEntities) {
        viewer.entities.remove(entity);
      }
      this.drawingPointEntities = [];
      this.drawingPolylineEntities = [];

      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      this.moveEntities = [];

      // 길이 라벨 제거
      this.lengthLabelEntity.remove();
      this.lengthLabelEntity = undefined;

      // POINT - 모든 포인트 삭제
      this.measurementLengthHorizontal.removePointEntity(
        this.measurementLengthHorizontal.pointEntities[
          this.measurementLengthHorizontal.pointEntities.length - 1
        ]
      );

      // POSITIONS - 마지막 좌표값 삭제
      this.measurementLengthHorizontal.removePositions(
        this.measurementLengthHorizontal.positions[
          this.measurementLengthHorizontal.positions.length - 1
        ]
      );
    }
  }
}
export default InteractionLengthHorizontal;
